import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Platform, ActivityIndicator, useWindowDimensions, } from 'react-native';
import LoadDetailsMobileView from './LoadDetailsMobileView';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../../theme/useTheme';
import { FontFamily } from '../../../../../constants/fonts';
import { ASNLoadDetailRow } from '../api/asnOverviewKpiApi';
import TableSkeleton from '../../../components/TableSkeleton';
import {
  LOAD_DETAILS_COLUMNS as COLUMNS,
  LoadDetailsColumnDef as ColumnDef,
  LoadDetailsColumnKey as ColumnKey,
  formatLoadDetailsCellValue as formatCellValue,
} from '../constants/loadDetailsColumns';

const MOBILE_TOOLBAR_BREAKPOINT = 640;
// Debounce the search box before firing the (server-side) search request, so
// a request isn't sent on every keystroke.
const SEARCH_DEBOUNCE_MS = 350;

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const LINK_COLUMNS: ColumnKey[] = ['shipName', 'recvName'];

const DEFAULT_EMPTY_MESSAGE = 'No load data available for the selected filters';

export interface LoadDetailsProps {
  /** Current page's rows, as returned by `POST /api/asn/load-details/list`. */
  rows: ASNLoadDetailRow[];
  totalCount: number;
  /** 1-based current page number. */
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  /** Active backend sort field (matches a `ColumnDef.field` value). */
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  loading?: boolean;
  hasData?: boolean;
  /** `response.message` — rendered verbatim in the empty state. */
  message?: string;
  searchText: string;
  onSearchChange: (searchText: string) => void;
  onPageChange: (pageNumber: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
  /** Exports every row matching the currently applied filters/sort/search
   * (all pages, not just the visible one) to an .xlsx file. */
  onExport: () => void;
  /** True while an export is in flight — disables the Export button and
   * shows a loading indicator so duplicate export requests can't be fired. */
  isExporting?: boolean;
}

const LoadDetails: React.FC<LoadDetailsProps> = ({
  rows,
  totalCount,
  pageNumber,
  pageSize,
  totalPages,
  sortBy,
  sortDirection,
  loading = false,
  hasData = true,
  message,
  searchText,
  onSearchChange,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onExport,
  isExporting = false,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { width: windowWidth } = useWindowDimensions();
  const isMobileToolbar = windowWidth < MOBILE_TOOLBAR_BREAKPOINT;

  // Local, immediately-responsive copy of the search text — the parent
  // (and therefore the API request) only receives the committed value after
  // the user pauses typing, via the debounce effect below.
  const [searchInput, setSearchInput] = useState(searchText);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the local input in sync if the committed search text ever changes
  // from outside this component (e.g. a future Reset that clears search).
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left?: number; right?: number; width: number } | null>(
    null,
  );
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const cardRef = useRef<any>(null);
  const columnsAnchorRef = useRef<any>(null);
  const columnsPopoverRef = useRef<any>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(() =>
    COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<ColumnKey, boolean>),
  );

  const visibleColumnDefs = COLUMNS.filter((col) => visibleColumns[col.key]);
  const activeSortColumnKey = useMemo(() => COLUMNS.find((col) => col.field === sortBy)?.key, [sortBy]);

  const handleSort = (col: ColumnDef) => {
    const isSameColumn = col.field === sortBy;
    // Toggling the same column flips direction; picking a new column
    // defaults to DESC (matches the table's overall default sort).
    const newDirection: 'ASC' | 'DESC' = isSameColumn ? (sortDirection === 'ASC' ? 'DESC' : 'ASC') : 'DESC';
    onSortChange(col.field as string, newDirection);
  };

  const handleSearchInputChange = (text: string) => {
    setSearchInput(text);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      onSearchChange(text);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchInputFocus = () => {
    setIsSearchInputFocused(true);
  };

  const handleSearchInputBlur = () => {
    setIsSearchInputFocused(false);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handlePageSizeChange = (size: number) => {
    if (size === pageSize) return;
    onPageSizeChange(size);
  };

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const webInputFocusReset =
      Platform.OS === "web"
        ? ({
            outlineStyle: "none",
            outlineWidth: 0,
            outlineColor: "transparent",
            boxShadow: "none",
          } as any)
        : {};

  // The Columns dropdown is rendered as an absolutely-positioned child of the card (its
  // "container"), not the page/viewport, and never uses position:fixed. We measure the
  // button and the card once, on open, and store the DELTA between them (not raw viewport
  // coordinates), so the dropdown tracks the button correctly and moves together with it
  // whenever the page/table scrolls — no scroll listener/recompute is needed because the
  // delta between two elements that scroll together as one unit never changes.
  const handleToggleColumnPicker = useCallback(() => {
    if (isColumnPickerOpen) {
      setIsColumnPickerOpen(false);
      return;
    }
    const anchorNode = columnsAnchorRef.current;
    const cardNode = cardRef.current;
    const dropdownWidth = Math.min(240, windowWidth - 32);
    if (anchorNode?.measureInWindow && cardNode?.measureInWindow) {
      anchorNode.measureInWindow((ax: number, ay: number, aw: number, ah: number) => {
        cardNode.measureInWindow((cx: number, cy: number, cw: number) => {
          const top = ay - cy + ah + 4;
          const fitsRight = ax - cx + dropdownWidth <= cw - 8;
          setDropdownPos(
            fitsRight
              ? { top, left: ax - cx, width: dropdownWidth }
              : { top, right: cx + cw - (ax + aw), width: dropdownWidth },
          );
          setIsColumnPickerOpen(true);
        });
      });
    } else {
      setDropdownPos({ top: 44, left: 0, width: dropdownWidth });
      setIsColumnPickerOpen(true);
    }
  }, [isColumnPickerOpen, windowWidth]);

  // Close on outside click / Escape (web only — the scrim below handles native taps-outside).
  useEffect(() => {
    if (!isColumnPickerOpen || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const anchorNode = columnsAnchorRef.current;
      const popoverNode = columnsPopoverRef.current;
      if (target && ((anchorNode?.contains && anchorNode.contains(target)) || (popoverNode?.contains && popoverNode.contains(target)))) {
        return;
      }
      setIsColumnPickerOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnPickerOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isColumnPickerOpen]);

  // Collapse on resize/orientation change so a stale measured position is never shown.
  useEffect(() => {
    if (!isColumnPickerOpen) return;
    setIsColumnPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  const hasRows = hasData && rows.length > 0;
  const startRow = !hasRows ? 0 : (pageNumber - 1) * pageSize + 1;
  const endRow = !hasRows ? 0 : startRow + rows.length - 1;

  // On mobile the web table is replaced by card-based LoadDetailsMobileView.
  if (isMobileToolbar) {
    return (
      <LoadDetailsMobileView
        rows={rows}
        loading={loading}
        hasData={hasData}
        message={message}
        totalCount={totalCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    );
  }

  return (
    <View style={styles.card} ref={cardRef}>
      <View style={styles.cardHead}>
        <View style={styles.cardHeadTitleGroup}>
          <Text style={styles.cardTitle}>Load Details</Text>
          <Text style={styles.cardSub}>
            One row per load. Default sort is Received Date descending, then Shipped Date. Export always contains all
            23 columns for the full filter scope.
          </Text>
        </View>

        <View style={styles.cardActions}>
          <View style={[styles.searchWrap, isSearchInputFocused && styles.searchWrapFocused, loading && styles.secondaryBtnDisabled]}>
            <MaterialIcons name="search" size={18} color="#838383" />
            <TextInput
              style={[styles.searchInput, webInputFocusReset, isSearchInputFocused && styles.searchInputFocused]}
              value={searchInput}
              onChangeText={handleSearchInputChange}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              editable={!loading}
              placeholder="PO / BOL, delivery, Con Ops, location"
              placeholderTextColor="#9AA1AB"
              testID="asn-gap-load-details-search"
            />
          </View>

          <View style={styles.columnsAnchor} ref={columnsAnchorRef}>
            <TouchableOpacity
              style={[styles.secondaryBtn, loading && styles.secondaryBtnDisabled]}
              onPress={handleToggleColumnPicker}
              disabled={loading}
              accessibilityRole="button"
              accessibilityLabel="Columns"
              testID="asn-gap-load-details-columns"
            >
              <MaterialIcons name="tune" size={16} color="#005CBC" />
              <Text style={styles.secondaryBtnText}>Columns</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[
              styles.secondaryBtn,
              (isExporting || loading) && styles.secondaryBtnDisabled,
            ]}
            onPress={onExport}
            disabled={isExporting || loading}
            accessibilityRole="button"
            accessibilityLabel="Export to Excel"
            testID="asn-gap-load-details-export"
          >
            {isExporting ? (
              <>
                <ActivityIndicator size="small" color="#005CBC" />
                <Text style={styles.secondaryBtnText}>Exporting...</Text>
              </>
            ) : (
              <>
                <MaterialIcons name="file-download" size={16} color="#005CBC" />
                <Text style={styles.secondaryBtnText}>Export to Excel</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {visibleColumnDefs.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>
            Select at least one column using the Columns button.
          </Text>
        </View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={styles.tableScroll}>
          <View>
            <View style={styles.headerRow}>
              {visibleColumnDefs.map((col) => {
                const isSorted = activeSortColumnKey === col.key;
                return (
                  <TouchableOpacity
                    key={col.key}
                    style={[styles.headerCellWrap, col.numeric ? styles.numColWidth : styles.dimColWidth]}
                    onPress={() => handleSort(col)}
                    testID={`asn-gap-load-details-sort-${col.key}`}
                  >
                    <Text style={styles.headerCell}>{col.label}</Text>
                    <MaterialIcons
                      name={isSorted ? (sortDirection === 'ASC' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                      size={16}
                      color={isSorted ? '#005CBC' : '#9AA1AB'}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>

            {loading ? (
              <TableSkeleton rows={Math.min(pageSize, 10)} />
            ) : !hasRows ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>{message || DEFAULT_EMPTY_MESSAGE}</Text>
              </View>
            ) : (
              rows.map((row, idx) => (
                <View key={`${row.poBoLNumber ?? 'row'}-${idx}`} style={styles.dataRow}>
                  {visibleColumnDefs.map((col) => {
                    if (col.key === 'asn') {
                      const rawValue = formatCellValue(row.asnAvailable);
                      const normalized = rawValue.toLowerCase();
                      if (normalized === 'yes' || normalized === 'no') {
                        const isNo = normalized === 'no';
                        return (
                          <View key={col.key} style={[styles.cellWrap, styles.dimColWidth]}>
                            <View
                              style={[
                                styles.tag,
                                isNo
                                  ? [styles.tagNo, isDark && styles.tagNoDark]
                                  : [styles.tagYes, isDark && styles.tagYesDark],
                              ]}
                            >
                              <Text
                                style={[
                                  styles.tagText,
                                  isNo
                                    ? [styles.tagTextNo, isDark && styles.tagTextNoDark]
                                    : [styles.tagTextYes, isDark && styles.tagTextYesDark],
                                ]}
                              >
                                {rawValue}
                              </Text>
                            </View>
                          </View>
                        );
                      }
                      return (
                        <View key={col.key} style={[styles.cellWrap, styles.dimColWidth]}>
                          <Text style={styles.cell}>{rawValue}</Text>
                        </View>
                      );
                    }
                    const value = row[col.field];
                    const isLink = LINK_COLUMNS.includes(col.key);
                    return (
                      <Text
                        key={col.key}
                        style={[
                          styles.cell,
                          col.numeric ? styles.numColWidth : styles.dimColWidth,
                          isLink && styles.linkText,
                        ]}
                      >
                        {formatCellValue(value, col.numeric)}
                      </Text>
                    );
                  })}
                </View>
              ))
            )}
          </View>
        </ScrollView>
      )}

      {visibleColumnDefs.length > 0 && (
        <View style={styles.tableFooter}>
          <Text style={styles.footerText}>
            {hasRows ? `${startRow} to ${endRow} of ${totalCount} loads` : `0 of ${totalCount} loads`}
          </Text>

          {totalPages > 1 && (
            <View style={styles.pagerCenter}>
            <View style={styles.pager}>
              {/* First page */}
              <TouchableOpacity
                style={[styles.pagerIconBtn, (pageNumber <= 1 || loading) && styles.pagerBtnDisabled]}
                onPress={() => onPageChange(1)}
                disabled={pageNumber <= 1 || loading}
                testID="asn-gap-load-details-page-first"
              >
                <MaterialIcons name="first-page" size={18} color={pageNumber <= 1 ? '#B0B7C3' : '#034895'} />
              </TouchableOpacity>
              {/* Previous page */}
              <TouchableOpacity
                style={[styles.pagerIconBtn, (pageNumber <= 1 || loading) && styles.pagerBtnDisabled]}
                onPress={() => onPageChange(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1 || loading}
                testID="asn-gap-load-details-page-prev"
              >
                <MaterialIcons name="chevron-left" size={18} color={pageNumber <= 1 ? '#B0B7C3' : '#034895'} />
              </TouchableOpacity>

              <Text style={styles.pagerPageLabel}>Page</Text>
              <View style={styles.pagerPageBox}>
                <Text style={styles.pagerPageBoxText}>{pageNumber}</Text>
              </View>
              <Text style={styles.pagerPageLabel}>{`of ${totalPages}`}</Text>

              {/* Next page */}
              <TouchableOpacity
                style={[styles.pagerIconBtn, (pageNumber >= totalPages || loading) && styles.pagerBtnDisabled]}
                onPress={() => onPageChange(Math.min(totalPages, pageNumber + 1))}
                disabled={pageNumber >= totalPages || loading}
                testID="asn-gap-load-details-page-next"
              >
                <MaterialIcons name="chevron-right" size={18} color={pageNumber >= totalPages ? '#B0B7C3' : '#034895'} />
              </TouchableOpacity>
              {/* Last page */}
              <TouchableOpacity
                style={[styles.pagerIconBtn, (pageNumber >= totalPages || loading) && styles.pagerBtnDisabled]}
                onPress={() => onPageChange(totalPages)}
                disabled={pageNumber >= totalPages || loading}
                testID="asn-gap-load-details-page-last"
              >
                <MaterialIcons name="last-page" size={18} color={pageNumber >= totalPages ? '#B0B7C3' : '#034895'} />
              </TouchableOpacity>
            </View>
            </View>
          )}

          <View style={styles.pageSizeRow}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.pageSizeBtn, pageSize === size && styles.pageSizeBtnActive, loading && styles.pagerBtnDisabled]}
                onPress={() => handlePageSizeChange(size)}
                disabled={loading}
                testID={`asn-gap-load-details-page-size-${size}`}
              >
                <Text style={[styles.pageSizeBtnText, pageSize === size && styles.pageSizeBtnTextActive]}>{size}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {isColumnPickerOpen && dropdownPos && (
        <>
          {/* Dims the table (not the toolbar) so the dropdown reads clearly in front of it, and
              gives native/mobile a tap-outside-to-close target (the web listener above covers
              clicks on the toolbar itself). This scrim is absolutely positioned within the card
              — never viewport-fixed — so it scrolls together with the page. */}
          <TouchableOpacity
            style={[styles.columnScrim, { top: dropdownPos.top }]}
            activeOpacity={1}
            onPress={() => setIsColumnPickerOpen(false)}
            testID="asn-gap-load-details-columns-scrim"
          />
          <View
            ref={columnsPopoverRef}
            style={[
              styles.columnPopover,
              {
                top: dropdownPos.top,
                width: dropdownPos.width,
                ...(dropdownPos.left !== undefined ? { left: dropdownPos.left } : { right: dropdownPos.right }),
              },
            ]}
            testID="asn-gap-load-details-columns-popover"
          >
            <ScrollView style={styles.columnPopoverList}>
              {COLUMNS.map((col) => (
                <TouchableOpacity
                  key={col.key}
                  style={styles.columnPopoverItem}
                  onPress={() => toggleColumn(col.key)}
                  testID={`asn-gap-load-details-column-${col.key}`}
                >
                  <MaterialIcons
                    name={visibleColumns[col.key] ? 'check-box' : 'check-box-outline-blank'}
                    size={18}
                    color={visibleColumns[col.key] ? '#005CBC' : '#9AA1AB'}
                  />
                  <Text style={styles.columnPopoverItemText}>{col.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    paddingTop: 20,
    paddingBottom: 12,
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardHead: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    paddingHorizontal: 20,
    paddingBottom: 14,
  },
  cardHeadTitleGroup: {
    flex: 1,
    minWidth: 260,
  },
  cardTitle: {
    fontSize: 17,
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
  },
  cardSub: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#838383',
    marginTop: 3,
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cardActionsMobile: {
    width: '100%',
    flexWrap: 'wrap',
    gap: 8,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  searchWrapMobile: {
    flex: 1,
    minWidth: 120,
  },
  searchWrapFocused: {
    // Keep the wrapper's border visible on focus - don't hide it
  },
  searchInput: {
    width: 220,
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#1A1D21',
    ...(Platform.OS === 'web' ? { outline: 'transparent' } : {}),
  },
  searchInputFocused: {
    fontFamily: FontFamily.regular,
    ...(Platform.OS === 'web' ? { outline: 'transparent' } : {}),
  },
  searchInputMobile: {
    width: undefined,
    flex: 1,
    minWidth: 0,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
  },
  secondaryBtnMobile: {
    paddingHorizontal: 10,
    gap: 4,
  },
  secondaryBtnDisabled: {
    opacity: 0.6,
  },
  secondaryBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#005CBC',
  },
  columnsAnchor: {
    position: 'relative',
  },
  columnScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.15)',
    zIndex: 15,
  },
  columnPopover: {
    position: 'absolute',
    maxHeight: 320,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 8,
    paddingVertical: 6,
    shadowColor: '#3A3A3A',
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
    zIndex: 20,
  },
  columnPopoverList: {
    maxHeight: 320,
  },
  columnPopoverItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  columnPopoverItemText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#1A1D21',
    flexShrink: 1,
  },
  tableScroll: {
    paddingHorizontal: 20,
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    paddingBottom: 10,
  },
  dataRow: {
    flexDirection: 'row',
    minHeight: 46,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#EBEBEB',
  },
  headerCellWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingRight: 12,
  },
  headerCell: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#616161',
    textTransform: 'uppercase',
  },
  cellWrap: {
    justifyContent: 'center',
    paddingRight: 12,
  },
  cell: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#1A1D21',
    paddingRight: 12,
  },
  dimColWidth: {
    width: 170,
  },
  numColWidth: {
    width: 110,
    textAlign: 'right',
  },
  linkText: {
    color: '#005CBC',
    fontFamily: FontFamily.medium,
  },
  tag: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  tagYes: {
    backgroundColor: '#CAF3C6',
  },
  tagYesDark: {
    backgroundColor: 'rgba(56, 182, 56, 0.22)',
    borderWidth: 1,
    borderColor: 'rgba(74, 222, 128, 0.35)',
  },
  tagNo: {
    backgroundColor: '#FFE5DE',
  },
  tagNoDark: {
    backgroundColor: 'rgba(220, 53, 53, 0.22)',
    borderWidth: 1,
    borderColor: 'rgba(248, 113, 113, 0.35)',
  },
  tagText: {
    fontSize: 11.5,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  tagTextYes: {
    color: '#0C420B',
  },
  tagTextYesDark: {
    color: '#8FEBA8',
  },
  tagTextNo: {
    color: '#AB2B09',
  },
  tagTextNoDark: {
    color: '#FFB09A',
  },
  emptyState: {
    paddingVertical: 28,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 13.5,
    fontFamily: FontFamily.regular,
    fontStyle: 'italic',
    color: '#838383',
  },
  tableFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  footerText: {
    fontSize: 12.5,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
  },
  pagerCenter: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    pointerEvents: 'box-none',
  } as any,
  pager: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  pagerIconBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerPageLabel: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
    marginHorizontal: 2,
  },
  pagerPageBox: {
    height: 28,
    minWidth: 32,
    paddingHorizontal: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#005CBC',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  pagerPageBoxText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
  },
  pageSizeRow: {
    flexDirection: 'row',
    gap: 4,
  },
  pageSizeBtn: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  pageSizeBtnActive: {
    backgroundColor: '#EFF5FC',
    borderColor: '#005CBC',
  },
  pageSizeBtnText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6A6D71',
  },
  pageSizeBtnTextActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
});

export default LoadDetails;
